import { Card, Tabs } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import SlidersHorizontalIcon from '@/comp/common/icons/SlidersHorizontalIcon';
import { EPButton } from '@/components';
import { useStores } from '@/hooks/use-stores';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import ShutdownAlertIcon from '@/comp/common/icons/ShutdownAlertIcon';
import WarningAlertIcon from '@/comp/common/icons/WarningAlertIcon';
import ServiceAlertIcon from '@/comp/common/icons/ServiceAlertIcon';
import BoxArrowDownIcon from '@/comp/common/icons/BoxArrowDownIcon';

import AlertsList from './AlertsList/AlertsList';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';

const deviceTypesWithAlerts = [
  ...compressorTypes,
  DeviceType.Booster,
  DeviceType.FlowSensor,
  DeviceType.PressureSensor,
  DeviceType.DewpointSensor,
  DeviceType.WaterTemperatureSensor,
  DeviceType.AirTemperatureSensor,
  DeviceType.DewpointAndPressureSensor,
  DeviceType.FlowAndPressureSensor,
];

const Alerts = observer(({ entity }: { entity: Entity }) => {
  const params = useParams();
  const { t } = useTranslation();
  const { alertStore } = useStores();

  if (!deviceTypesWithAlerts.includes(entity.deviceType as DeviceType)) return null;

  const alerts = alertStore.filterAlertsByDeviceId(entity.deviceId);
  const activeAlerts = alertStore.filterActiveAlerts(alerts);
  const archivedAlerts = alertStore.filterArchivedAlerts(alerts);
  const shutdownAlerts = alertStore.filterShutdownAlerts(activeAlerts);
  const warningAlerts = alertStore.filterWarningAlerts(activeAlerts);
  const serviceAlerts = alertStore.filterServiceAlerts(activeAlerts);

  const openAlerts = alertStore.filterOpenAlerts(activeAlerts);
  const closedAlerts = alertStore.filterClosedAlerts(activeAlerts);
  const openShutdownAlerts = alertStore.filterOpenAlerts(shutdownAlerts);
  const openWarningAlerts = alertStore.filterOpenAlerts(warningAlerts);
  const openServiceAlerts = alertStore.filterOpenAlerts(serviceAlerts);
  const closedShutdownAlerts = alertStore.filterClosedAlerts(shutdownAlerts);
  const closedWarningAlerts = alertStore.filterClosedAlerts(warningAlerts);
  const closedServiceAlerts = alertStore.filterClosedAlerts(serviceAlerts);

  const tabItems = [
    {
      key: 'all',
      label: (
        <div className="alert-tab-label">
          {t('All')} {openAlerts.length} ({closedAlerts.length})
        </div>
      ),
      children: <AlertsList alerts={activeAlerts} />,
    },
    {
      key: 'shutdown',
      label: (
        <div className="alert-tab-label">
          <ShutdownAlertIcon />
          {t('Shutdown')} {openShutdownAlerts.length} ({closedShutdownAlerts.length})
        </div>
      ),
      children: <AlertsList alerts={shutdownAlerts} />,
    },
    {
      key: 'warning',
      label: (
        <div className="alert-tab-label">
          <WarningAlertIcon />
          {t('Warning')} {openWarningAlerts.length} ({closedWarningAlerts.length})
        </div>
      ),
      children: <AlertsList alerts={warningAlerts} />,
    },
    {
      key: 'service',
      label: (
        <div className="alert-tab-label">
          <ServiceAlertIcon />
          {t('Service')} {openServiceAlerts.length} ({closedServiceAlerts.length})
        </div>
      ),
      children: <AlertsList alerts={serviceAlerts} />,
    },
    {
      key: 'archived',
      label: (
        <div className="alert-tab-label archived">
          <BoxArrowDownIcon />
          {t('Archived')}
        </div>
      ),
      children: <AlertsList alerts={archivedAlerts} archived />,
    },
  ];

  return (
    <Card
      title={t('alert')}
      extra={
        <Link to={`/home/station/${params.stationId}/rules`}>
          <EPButton className="sliders-horizontal-icon" icon={<SlidersHorizontalIcon />} type="text" />
        </Link>
      }
    >
      <Tabs items={tabItems} tabBarGutter={0} />
    </Card>
  );
});

export default Alerts;
